// Etiqueta QR de rollo de tela: 10 x 8 cm (100 mm a lo ancho del rollo de etiquetas,
// 80 mm de largo por etiqueta). Una hoja del PDF = una etiqueta, para que no se
// encime con la siguiente.
//
// El contenido va girado 90° (se lee a lo largo de la etiqueta), igual que la etiqueta
// que ya usaban. Se diseña en un "marco de lectura" de 80 mm de ancho (u) x 100 mm de
// alto (v) y se traslada a la hoja: x = v, y = 80 - u.
import { jsPDF } from "jspdf";
import qrcode from "qrcode-js";
import { abrirPDF } from "./abrirPDF";

const ANCHO = 100; // mm, a lo ancho del rollo
const LARGO = 80; // mm, largo de cada etiqueta

const cargar = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Gira la imagen 90° a la izquierda (y la reduce para que el PDF no pese de más).
// Sale en JPEG con fondo blanco: jsPDF incrusta el JPEG tal cual, mientras que los PNG
// con transparencia del canvas los decodifica mal (salían rayados).
function girar(img, maxLado = 500) {
  if (!img) return null;
  const esc = Math.min(1, maxLado / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * esc);
  const h = Math.round(img.naturalHeight * esc);
  const c = document.createElement("canvas");
  c.width = h;
  c.height = w;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.translate(0, w);
  ctx.rotate(-Math.PI / 2);
  ctx.drawImage(img, 0, 0, w, h);
  return c.toDataURL("image/jpeg", 0.92);
}

// Imagen ya girada, colocada en el marco de lectura (u, v, ancho, alto).
// alias: los logos se guardan UNA vez en el PDF aunque salgan en cada etiqueta.
const imagen = (doc, data, u, v, w, h, alias) => {
  if (data) doc.addImage(data, "JPEG", v, LARGO - u - w, h, w, alias);
};

// Texto en el marco de lectura; v es la línea base
const texto = (doc, str, u, v) => doc.text(String(str), v, LARGO - u, { angle: 90 });

const limpio = (v) => (v == null || v === "" ? "NA" : v);

let logosCache = null;
async function logos() {
  if (!logosCache) {
    const [gavini, lynqro] = await Promise.all([
      cargar(process.env.PUBLIC_URL + "logoInstitucion.png"),
      cargar(process.env.PUBLIC_URL + "Logo-Lynqro-Negro.png"),
    ]);
    logosCache = { gavini: girar(gavini), lynqro: girar(lynqro) };
  }
  return logosCache;
}

/**
 * rollos: [{ id, codigo, nombre, proveedor, factura, color, cantidad, unidad }]
 * (proveedor y factura son opcionales). Devuelve el jsPDF.
 */
export async function etiquetasRollosPDF(rollos) {
  const { gavini, lynqro } = await logos();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [ANCHO, LARGO] });

  for (const [i, r] of (rollos || []).entries()) {
    if (i > 0) doc.addPage([ANCHO, LARGO], "landscape");

    // Encabezado: logo Gavini y Lynqro
    imagen(doc, gavini, 3, 3, 38, 15.8, "logoGavini");
    imagen(doc, lynqro, 52, 6, 25, 9.4, "logoLynqro");

    // QR
    const qr = await cargar(qrcode.toDataURL(String(r.id)));
    imagen(doc, girar(qr, 300), 3, 22, 35, 35, `qr${i}`);

    // Datos a la derecha del QR (con salto de línea si no caben)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const anchoTexto = 37;
    const lineas = [
      `Cod ${limpio(r.codigo)}`,
      `Mod ${limpio(r.nombre)}`,
      r.color != null ? `Color ${limpio(r.color)}` : null,
      r.proveedor != null ? `Prov. ${limpio(r.proveedor)}` : null,
      r.factura != null ? `Factura ${limpio(r.factura)}` : null,
    ]
      .filter(Boolean)
      .flatMap((l) => doc.splitTextToSize(l, anchoTexto));
    let v = 26;
    for (const l of lineas.slice(0, 8)) {
      texto(doc, l, 41, v);
      v += 4.1;
    }

    // ID del rollo (lo que lee el QR)
    doc.setFontSize(7);
    texto(doc, `ID ${r.id}`, 3, 64);

    // Cantidad grande
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    const cant = doc.splitTextToSize(`Cant. ${limpio(r.cantidad)} ${r.unidad || ""}`.trim(), 74);
    texto(doc, cant[0], 3, 80);
    if (cant[1]) texto(doc, cant[1], 3, 89);
  }
  return doc;
}

// Abre el PDF en otra pestaña listo para imprimir
export async function abrirEtiquetasRollos(rollos) {
  if (!rollos || !rollos.length) return;
  const doc = await etiquetasRollosPDF(rollos);
  abrirPDF(doc);
}
